import type { Resource, ResourceKey } from '@eaphone/types/resources';

import { Ky, KyJson } from './index.ts';
import type { RawList } from './lib.d.ts';
import type { CustomOptions } from './type.d.ts';

export const Network = Object.freeze({
  resource: <key extends ResourceKey, Struct extends Resource[key]>(
    resource: key,
  ) =>
    Object.freeze({
      select: Object.freeze({
        id: (id: string) =>
          KyJson.get<{ data: Struct & { id: string } }>(
            `${resource}/${id}`,
          ).then(({ data }) => data),
        batchId: (batchId: string) =>
          KyJson.get<{ data: Struct & { id: string } }>(
            `${resource}/batchId/${batchId}/`,
          ).then(({ data }) => data),
        serial: (serial: string) =>
          KyJson.get<{ data: Struct & { id: string } }>(
            `${resource}/serial/${serial}/`,
          ).then(({ data }) => data),
      }),
    }),
  custom: Object.freeze({
    get: <key extends ResourceKey, Struct extends Resource[key]>(
      resource: key,
      options?: CustomOptions,
    ) =>
      KyJson.get<{ data: Struct & { id: string } }>(resource, options).then(
        ({ data }) => data,
      ),
    file: <key extends ResourceKey, Struct extends Resource[key]>(
      resource: key,
      options?: CustomOptions & { file: File },
    ) => KyJson.file<Struct>(resource, options),
    post: <key extends ResourceKey, Struct extends Resource[key]>(
      resource: key,
      options?: CustomOptions & {
        json?: Partial<Struct>;
      },
    ) =>
      KyJson.post<{ data: Struct & { id: string } }>(resource, options).then(
        ({ data }) => data,
      ),
    action: <key extends ResourceKey, Struct extends Resource[key]>(
      resource: key,
      options?: CustomOptions & { json?: Record<string, any> },
    ) =>
      options?.json
        ? KyJson.post<{ data: Struct & { id: string } }>(
            resource,
            options,
          ).then(({ data }) => data)
        : KyJson.get<{ data: Struct & { id: string } }>(resource, options).then(
            ({ data }) => data,
          ),
    patch: <key extends ResourceKey, Struct extends Resource[key]>(
      resource: key,
      options?: CustomOptions &
        ({ json: Partial<Struct> } | { searchParams?: Record<string, any> }),
    ) =>
      KyJson.patch<{ data: Struct & { id: string } }>(resource, options).then(
        ({ data }) => data,
      ),
    delete: <key extends ResourceKey>(resource: key, options?: CustomOptions) =>
      KyJson.delete<never>(resource, options),
    list: <key extends ResourceKey, Struct extends Resource[key]>(
      resource: key,
      options?: CustomOptions & {
        searchParams:
          | {
              sort?: 'DESC' | 'ASC';
              sortField?: string;
              page_index: number;
              page_size: number;
            }
          | {
              sort: 'DESC' | 'ASC';
              sortField?: string;
            };
      },
    ) =>
      KyJson.get<RawList<Struct & { id: string }>>(
        `${resource}/`,
        options,
      ).then(({ data: list, ...rest }) => ({ ...rest, list })),
  }),
  raw: Object.freeze({
    get: <key extends ResourceKey>(resource: key, options?: CustomOptions) =>
      Ky.get(resource, options),
    post: <key extends ResourceKey>(resource: key, options?: CustomOptions) =>
      Ky.post(resource, options),
    patch: <key extends ResourceKey>(resource: key, options: CustomOptions) =>
      Ky.patch(resource, options),
  }),
});
